/**
 * Inline SVG micro-sparkline for per-row economy history (v0.40.0).
 *
 * Consumes an array of numbers (one per historical cycle) and returns
 * an `<svg>` HTML string sized for inline placement next to a balance
 * row — 44 × 14 px by default. Uses a polyline path with a final dot
 * marking the latest value.
 *
 * Missing data handling: arrays with < 2 points render an em-dash
 * placeholder so the row still aligns cleanly with sparkline-bearing
 * neighbours. Flat data (all equal) renders a centered line.
 *
 * Color is passed through — caller decides green vs amber vs red to
 * match the category's legal/dirty/expense role.
 */

export interface MiniSparklineOpts {
  width?: number;   // default 44
  height?: number;  // default 14
  color?: string;   // default '--text-mid'
  title?: string;   // tooltip text (plain string)
}

export function renderMiniSparkline(values: number[], opts: MiniSparklineOpts = {}): string {
  const w = opts.width ?? 44;
  const h = opts.height ?? 14;
  const color = opts.color ?? 'currentColor';
  const title = opts.title ? `<title>${escapeAttr(opts.title)}</title>` : '';

  // Fewer than 2 points: no trend to render.
  if (!values || values.length < 2) {
    return `<svg class="eco-mini-spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">${title}<text x="${w / 2}" y="${h / 2 + 3}" text-anchor="middle" font-size="10" fill="${color}" opacity="0.35">\u2013</text></svg>`;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  // Flat series: render a single horizontal line at mid-height.
  if (range === 0) {
    const y = h / 2;
    return `<svg class="eco-mini-spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">${title}<line x1="0" y1="${y}" x2="${w}" y2="${y}" stroke="${color}" stroke-width="1" opacity="0.55"/></svg>`;
  }

  // Normalize to a polyline inside a 1px padding margin.
  const pad = 1;
  const innerW = w - 2 * pad;
  const innerH = h - 2 * pad;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * innerW;
    const y = pad + innerH - ((v - min) / range) * innerH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  // Last point dot — marks the current cycle.
  const lastIdx = values.length - 1;
  const lastX = pad + innerW;
  const lastY = pad + innerH - ((values[lastIdx] - min) / range) * innerH;

  return `<svg class="eco-mini-spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">${title}<polyline fill="none" stroke="${color}" stroke-width="1.25" stroke-linejoin="round" stroke-linecap="round" points="${pts.join(' ')}" opacity="0.8"/><circle cx="${lastX.toFixed(1)}" cy="${lastY.toFixed(1)}" r="1.6" fill="${color}"/></svg>`;
}

/** Extract a per-category series from history snapshots in chronological
 *  order. Missing keys in a snapshot contribute 0 (matches server
 *  omitempty semantics). */
export function categorySeries(
  snapshots: Array<{ byCategory?: Record<string, number> }>,
  key: string,
): number[] {
  return snapshots.map(s => s.byCategory?.[key] ?? 0);
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
